<template>
    <div class="section">
        <div class="center-section">
            <div class="form-heading">
                <h1 class="header-1">{{ $t('auth.reset.heading') }}</h1>
                <p class="caption">
                    {{ $t('auth.reset.description') }}
                </p>
            </div>
           
            <div class="signup-form">
                <form @submit.prevent="handleSubmit">
                    <base-input class="form-group" 
                        type="email" 
                        :placeholder="$t('auth.email')"
                        required 
                        icon="envelop"
                        v-model="email"
                        :error="error">
                    </base-input>

                    <primary-btn :disabled="!isValid" :loading="isLoading">
                        {{ $t('auth.reset.sendLink') }}
                    </primary-btn>
                </form>
            </div>

            <div class="login-link caption">
                <router-link to="/login" class="link">
                    {{ $t('auth.reset.backToSignIn') }}
                </router-link>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import BaseInput from '@/components/BaseComponents/BaseInput.vue';
import PrimaryBtn from '@/components/BaseComponents/PrimaryBtn.vue';
import { authAPI } from '@/services/api';
import { trackEvent, trackFormSubmission, trackError } from '@/utils/analytics';
import { useI18n } from 'vue-i18n';

const email = ref('');
const error = ref('');
const isLoading = ref(false);

const router = useRouter();
const { t } = useI18n();

const isValid = computed(() => {
    return email.value && email.value.includes('@');
});

async function handleSubmit() {
    if (!isValid.value) {
        error.value = t('auth.reset.validEmail');
        return;
    }

    isLoading.value = true;
    error.value = '';

    trackEvent('password_reset_started', {
        email_domain: email.value.split('@')[1] || 'unknown'
    });

    try {
        await authAPI.resetPasswordEmail(email.value);
        trackEvent('password_reset_email_sent', {
            email_domain: email.value.split('@')[1] || 'unknown'
        });
        trackFormSubmission('password_reset_form', true);
        router.push('/resetpass/confirm');
    } catch (err) {
        error.value = err.message || t('auth.reset.failedToSend');
        trackEvent('password_reset_failed', {
            email_domain: email.value.split('@')[1] || 'unknown'
        });
        trackFormSubmission('password_reset_form', false);
        trackError('password_reset_error', err.message || 'Unknown error', 'password_reset_form');
    } finally {
        isLoading.value = false;
    }
}
</script>

<style scoped>
.section {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: var(--spacing-xl);
}

.center-section {
    width: 35%;
    min-width: 300px;
}

.form-heading {
    text-align: center;
    margin-bottom: var(--spacing-lg);
}

.form-heading h1 {
    margin-bottom: var(--spacing-md);
}

.form-heading .caption {
    color: var(--color-white-40);
    margin: 0 auto;
    max-width: var(--max-width-notification);
}

.signup-form {
    padding: var(--spacing-lg);
    background-color: var(--bg-card);
    border-radius: var(--border-radius-2xl);
    margin-bottom: var(--spacing-lg);
}

.form-group {
    margin-bottom: var(--spacing-lg);
}

.login-link {
    text-align: center;
    color: var(--color-white-40);
}

.link {
    color: var(--color-white);
    text-decoration: none;
    transition: opacity var(--transition-fast);
}

.link:hover {
    opacity: 0.8;
}

@media (max-width: 768px) {
    .center-section {
        width: 90%;
    }
}
</style>
